import type { ReactNode } from 'react';

import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';

import { PrimaryButton } from '@/lib/ui/atoms';
import { cn } from '@/lib/utils';

import styles from './Banner.module.scss';

// ── Types ─────────────────────────────────────────────────────────────────────

export type BannerVariant = 'info' | 'success' | 'warning' | 'destructive';

export interface BannerAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

// ── Variant config ────────────────────────────────────────────────────────────

const variantConfig = {
  info: {
    icon: Info,
    bgClass: '!bg-info/10',
    borderClass: 'border-b border-info/40',
    textClass: 'text-info',
    buttonVariant: 'info' as const,
  },
  success: {
    icon: CheckCircle2,
    bgClass: '!bg-success/10',
    borderClass: 'border-b border-success/40',
    textClass: 'text-success-dark dark:text-success',
    buttonVariant: 'success' as const,
  },
  warning: {
    icon: AlertTriangle,
    bgClass: '!bg-warning/10',
    borderClass: 'border-b border-warning/40',
    textClass: 'text-warning-dark dark:text-warning',
    buttonVariant: 'warning' as const,
  },
  destructive: {
    icon: XCircle,
    bgClass: '!bg-destructive/10',
    borderClass: 'border-b border-destructive/40',
    textClass: 'text-error-dark dark:text-destructive',
    buttonVariant: 'destructive' as const,
  },
} satisfies Record<
  BannerVariant,
  {
    icon: React.ComponentType<{ className?: string }>;
    bgClass: string;
    borderClass: string;
    textClass: string;
    buttonVariant: 'info' | 'success' | 'warning' | 'destructive';
  }
>;

// ── Component ─────────────────────────────────────────────────────────────────

interface BannerProps {
  variant?: BannerVariant;
  /** If provided, Banner renders a PrimaryButton with the matching variant. */
  action?: BannerAction;
  className?: string;
  children: ReactNode;
}

export function Banner({ variant = 'info', action, className, children }: BannerProps) {
  const { icon: Icon, bgClass, borderClass, textClass, buttonVariant } = variantConfig[variant];

  return (
    <div
      role="alert"
      className={cn(
        styles.banner,
        bgClass,
        borderClass,
        textClass,
        'flex items-center gap-3 px-4 py-2.5 text-sm',
        className,
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span className="flex-1">{children}</span>
      {action && (
        <PrimaryButton
          size="sm"
          variant={buttonVariant}
          onClick={action.onClick}
          disabled={action.disabled}
          className="shrink-0"
        >
          {action.label}
        </PrimaryButton>
      )}
    </div>
  );
}
